import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  ShieldCheck,
  Eye,
  Bell,
  GraduationCap,
  Network,
  Mail,
  Monitor,
  KeyRound,
  FileSearch,
  ClipboardCheck,
  Moon,
  Users,
  Lock,
  Skull,
  Database,
  UserX,
  MailWarning,
  Square,
  Phone
} from "lucide-react";

const CybersecurityPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetAssessment = () => navigate("/#contact");

  return (
    <div className="min-h-screen" data-testid="cybersecurity-page">
      <Header scrolled={scrolled} />
      <main>
        <PageHeader />
        <ThreatLandscape />
        <ApproachSection />
        <ServicesSection />
        <CommonThreats />
        <ProtectionChecklist />
        <FAQSection />
        <CTASection handleGetAssessment={handleGetAssessment} />
      </main>
      <Footer />
    </div>
  );
};

// Page Header Component
const PageHeader = () => (
  <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
    <div className="max-w-4xl mx-auto text-center">
      <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
        <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
        <span className="mx-2">›</span>
        <Link to="/services" className="hover:text-blue-600 transition-colors">Services</Link>
        <span className="mx-2">›</span>
        <span>Cybersecurity</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
        Cybersecurity Services
      </h1>
      <p className="text-lg max-w-2xl mx-auto" style={{ color: "#4A5568" }}>
        Protect your business from today's evolving threats
      </p>
    </div>
  </section>
);

// Threat Landscape Section
const ThreatLandscape = () => (
  <section className="py-20 px-5" style={{ backgroundColor: "#FEF2F2" }} data-testid="threat-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
        The Threats Are Real
      </h2>
      <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
        Cybercrime is growing—is your business protected?
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <StatCard number="60%" text="of small businesses close within 6 months of a major cyberattack" />
        <StatCard number="$4.45M" text="average cost of a data breach in 2023" />
        <StatCard number="Every 39 sec" text="a cyber attack occurs somewhere in the world" small />
      </div>
      <p className="text-xs text-center mt-6" style={{ color: "#9CA3AF" }}>
        Source: Industry cybersecurity reports
      </p>
    </div>
  </section>
);

const StatCard = ({ number, text, small }) => (
  <div className="bg-white border-2 rounded-xl p-10 text-center" style={{ borderColor: "#FCA5A5" }}>
    <p className={`font-bold mb-4 ${small ? 'text-4xl md:text-5xl' : 'text-5xl md:text-6xl'}`} style={{ color: "#DC2626" }}>
      {number}
    </p>
    <p className="text-base leading-relaxed" style={{ color: "#374151" }}>{text}</p>
  </div>
);

// Approach Section
const ApproachSection = () => (
  <section className="py-20 px-5 bg-white" data-testid="approach-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
        Comprehensive Protection for Your Business
      </h2>
      <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
        A multi-layered approach to cybersecurity
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <PillarCard 
          Icon={ShieldCheck} 
          title="Prevention" 
          color="#3B82F6"
          items={["Firewall management", "Anti-virus/anti-malware", "Email filtering", "Web filtering", "Access controls"]}
        />
        <PillarCard 
          Icon={Eye} 
          title="Detection" 
          color="#2C5F2D"
          items={["24/7 monitoring", "Threat intelligence", "Intrusion detection", "Vulnerability scanning", "Security alerts"]}
        />
        <PillarCard 
          Icon={Bell} 
          title="Response" 
          color="#3B82F6"
          items={["Incident response plan", "Threat containment", "Recovery procedures", "Forensic analysis", "Communication protocols"]}
        />
        <PillarCard 
          Icon={GraduationCap} 
          title="Training" 
          color="#2C5F2D"
          items={["Employee awareness", "Phishing simulations", "Security best practices", "Ongoing education", "Policy development"]}
        />
      </div>
    </div>
  </section>
);

const PillarCard = ({ Icon, title, color, items }) => (
  <div className="rounded-lg p-10" style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", borderLeft: `4px solid ${color}` }}>
    <Icon size={48} style={{ color }} className="mb-4" />
    <h3 className="text-2xl font-bold mb-5" style={{ color: "#1F2937" }}>{title}</h3>
    <ul className="space-y-3">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-center gap-3 text-base" style={{ color: "#4A5568" }}>
          <span style={{ color }}>•</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

// Services Section
const ServicesSection = () => (
  <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="services-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
        Our Cybersecurity Services
      </h2>
      <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
        Complete protection for your business
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <ServiceCard Icon={Network} title="Network Security" description="Advanced firewall configuration, intrusion prevention, and network segmentation" color="#3B82F6" />
        <ServiceCard Icon={Mail} title="Email Security" description="Spam filtering, phishing protection, and email encryption" color="#2C5F2D" />
        <ServiceCard Icon={Monitor} title="Endpoint Protection" description="Anti-virus, anti-malware, and endpoint detection and response (EDR)" color="#3B82F6" />
        <ServiceCard Icon={KeyRound} title="Multi-Factor Authentication" description="Add an extra layer of security to all your critical systems" color="#2C5F2D" />
        <ServiceCard Icon={FileSearch} title="Security Audits" description="Comprehensive assessments to identify vulnerabilities and compliance gaps" color="#3B82F6" />
        <ServiceCard Icon={ClipboardCheck} title="Compliance Support" description="Help meeting industry requirements (PIPEDA, HIPAA, PCI-DSS, etc.)" color="#2C5F2D" />
        <ServiceCard Icon={Moon} title="Dark Web Monitoring" description="Monitor the dark web for compromised credentials and data leaks" color="#3B82F6" />
        <ServiceCard Icon={Users} title="Security Awareness Training" description="Educate your team to recognize and avoid cyber threats" color="#2C5F2D" />
      </div>
    </div>
  </section>
);

const ServiceCard = ({ Icon, title, description, color }) => (
  <div className="bg-white rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
    <Icon size={40} style={{ color }} className="mb-3" />
    <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base" style={{ color: "#4A5568" }}>{description}</p>
  </div>
);

// Common Threats Section
const CommonThreats = () => (
  <section className="py-20 px-5 bg-white" data-testid="common-threats-section">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
        Common Cyber Threats Facing Businesses Today
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <ThreatCard Icon={Lock} title="Ransomware" description="Malicious software that locks your files and demands payment for release" />
        <ThreatCard Icon={Skull} title="Phishing" description="Fraudulent emails designed to steal credentials or install malware" />
        <ThreatCard Icon={Database} title="Data Breaches" description="Unauthorized access to sensitive customer or business information" />
        <ThreatCard Icon={UserX} title="Insider Threats" description="Security risks from employees, contractors, or business partners" />
        <ThreatCard Icon={MailWarning} title="Business Email Compromise" description="Scammers impersonating executives to authorize fraudulent transfers" />
      </div>
    </div>
  </section>
);

const ThreatCard = ({ Icon, title, description }) => (
  <div className="rounded-lg p-8" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #DC2626" }}>
    <Icon size={40} style={{ color: "#DC2626" }} className="mb-3" />
    <h3 className="text-xl font-bold mb-2" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base" style={{ color: "#4A5568" }}>{description}</p>
  </div>
);

// Protection Checklist Section
const ProtectionChecklist = () => {
  const items = [
    "Do you have up-to-date antivirus software?",
    "Are your firewalls properly configured?",
    "Do you require multi-factor authentication?",
    "Are your backups tested and encrypted?",
    "Have you trained employees on security awareness?",
    "Do you have an incident response plan?",
    "Are your systems regularly patched and updated?",
    "Do you monitor for suspicious activity?"
  ];

  return (
    <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="checklist-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ color: "#1F2937" }}>
          Is Your Business Protected?
        </h2>
        <div className="bg-white border-2 border-gray-200 rounded-xl p-8 md:p-12">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-4 py-3">
              <Square size={24} style={{ color: "#9CA3AF" }} />
              <span className="text-lg" style={{ color: "#374151" }}>{item}</span>
            </div>
          ))}
          <div className="mt-8 p-5 rounded-lg text-center" style={{ backgroundColor: "#FEF2F2" }}>
            <p className="text-lg font-bold" style={{ color: "#DC2626" }}>
              If you checked 'no' to any of these, your business is at risk. Let's talk.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    { q: "How vulnerable is my business?", a: "Every business connected to the internet is at risk. Small businesses are often targeted because they typically have weaker security than larger enterprises." },
    { q: "Can't we just use free antivirus software?", a: "Free antivirus is better than nothing, but it only addresses one aspect of security. Comprehensive protection requires multiple layers of defense." },
    { q: "What should we do if we're attacked?", a: "Having an incident response plan is critical. We help you prepare for, respond to, and recover from security incidents." },
    { q: "How much does cybersecurity cost?", a: "The cost varies based on your business size and needs, but it's far less expensive than recovering from a breach. We'll provide a custom quote." },
    { q: "Do we need cybersecurity insurance?", a: "Many businesses are adding cyber insurance as an additional layer of protection. We can help you understand what insurers require." }
  ];

  return (
    <section className="py-20 px-5 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
          Cybersecurity Questions Answered
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="rounded-lg p-6" style={{ backgroundColor: "#F9FAFB" }}>
              <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>Q: {faq.q}</h3>
              <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>A: {faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ handleGetAssessment }) => (
  <section className="py-20 px-5" style={{ background: "linear-gradient(to right, #DC2626, #991B1B)" }} data-testid="cta-section">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
        Don't Wait Until It's Too Late
      </h2>
      <p className="text-lg mb-10" style={{ color: "rgba(255,255,255,0.9)" }}>
        Protect your business with a comprehensive security assessment
      </p>
      <Button
        onClick={handleGetAssessment}
        data-testid="cta-btn"
        className="text-lg font-bold px-10 py-6 rounded-lg hover:bg-gray-100 transition-all"
        style={{ backgroundColor: "white", color: "#DC2626" }}
      >
        Get Your Free Security Assessment
      </Button>
      <p className="mt-5 text-white">
        <a href="tel:7783497449" className="inline-flex items-center gap-2 font-semibold hover:underline" data-testid="cta-phone">
          <Phone size={18} />
          Call (778) 349-7449
        </a>
      </p>
    </div>
  </section>
);

export default CybersecurityPage;
